import { HttpErrorResponse } from '@angular/common/http';
import { createAction, props } from '@ngrx/store';
import { PermsEvaluationResult } from '@servicesV2/permissions.service';
import {
  AccessibleTenant,
  AllocateEntitlementPayload,
  AllocateEntitlementResponse,
  ApplicationCard,
  ApplicationCardButtonAction,
  ArchiveTenantPayload,
  CommandRequestData,
  CreateChildTenant,
  CreateTenant,
  CreateTenantSuccess,
  EffectiveRole,
  Entitlement,
  EntitlementData,
  FavoriteApps,
  ICatalogInfo,
  ITenantDetail,
  LeaveTenantPayload,
  PageResponse,
  PendingService,
  Preferences,
  ResourceRoleAction,
  TenantHierarchyUsersRequest,
  TenantResourceHierarchy,
  TenantService,
  TenantUser,
  TenantUtilityTokens,
  UnallocatedEntitlementCounts,
  UpdateTenant,
  UploadLogoPreOperation,
  UserFeedback,
  UserNotificationCounts,
  UserPreferences,
  UserRole,
  UserTenant,
} from '../lemans-app.model';
import { UserRoleHierarchy } from '../user-access';
import {
  ChangeParentPayload,
  ConvertToParentOrganization,
  ConvertToSubOrganization,
  DeallocateEntitlementRequestBody,
  TransferOwnership,
  UserMembership,
} from './user.model';

export const emptyAction = createAction('[EMTPY] Empty Action');
export const userLogged = createAction('[USER DATA] User Logged');
export const userLoggedSuccess = createAction('[USER DATA] User Logged');
export const loadUserProfile = createAction('[USER DATA] Load User Profile');
export const setUserId = createAction('[USER DATA] Set User ID', props<{ payload: string }>());
export const getUserPreferences = createAction('[USER DATA] Get User Preferences');
export const getUserPreferencesSuccess = createAction(
  '[USER DATA] Get User Preferences Success',
  props<{ value: UserPreferences }>(),
);
export const userData = createAction('[USER DATA] Set User Data', props<{ value: UserPreferences }>());
export const setUserRoles = createAction('[USER DATA] Set User Roles', props<{ value: UserRole[] }>());
export const userNotFound = createAction('[USER DATA] User Not Found');
export const getUserPreferencesFailed = createAction(
  '[USER DATA] Get User Preferences Failed',
  props<{ value: HttpErrorResponse }>(),
);

export const getNotificationCounts = createAction('[USER DATA] Get User Notifications Count');
export const setNotificationCountsSuccess = createAction(
  '[USER DATA] Get User Notifications Count Success',
  props<{ payload: UserNotificationCounts }>(),
);

export const getUserNotifications = createAction('[USER DATA] Get User Notifications');

export const setCurrentLanguage = createAction(
  '[USER DATA] Set Current language',
  props<{ preferredLanguage: string }>(),
);

export const updatePreferredLanguage = createAction(
  '[USER DATA] Update Preffered language',
  props<{ preferredLanguage: string }>(),
);

// system tried to find and set tenant
export const setTenantId = createAction(
  '[TENANT] Set Current Tenant Id',
  props<{ value: string; navigateTo?: string }>(),
);

// user triggers from org switcher
export const switchTenant = createAction('[TENANT] Tenant Switched', props<{ value: string; navigateTo?: string }>());
export const setTenantLogo = createAction('[TENANT] Set Tenant Logo', props<{ tenantId: string; etag?: string }>());
// get tenant api call
export const getTenantDetails = createAction('[TENANT] Get Tenant Details', props<{ value: string }>());

export const setTenantDetails = createAction('[TENANT] Set Current Tenant', props<{ payload?: ITenantDetail }>());

export const setPersonalTenantContext = createAction('[TENANT] Set Personal Tenant Context');

export const getTenantEffectiveRolesAndServices = createAction(
  '[TENANT] Get Tenant Effective Roles and Services',
  props<{ tenantId: string; navigateTo?: string }>(),
);
export const setTenantEffectiveRoles = createAction(
  '[TENANT] Set Tenant Effective Roles',
  props<{ effectiveRoles: EffectiveRole[]; navigateTo?: string }>(),
);
export const createTenant = createAction(
  '[TENANT] Create Tenant',
  props<{
    payload: CreateTenant;
    blobImage: Blob;
    idempotencyKey: string;
  }>(),
);
export const createChildTenant = createAction(
  '[TENANT] Create Child Tenant',
  props<{
    payload: CreateChildTenant;
    blobImage: Blob;
    idempotencyKey: string;
    parentTenantName: string;
  }>(),
);
export const createTenantSuccess = createAction(
  '[TENANT] Create Tenant Success',
  props<{ payload: CreateTenantSuccess }>(),
);
export const setTenantCreated = createAction('[TENANT] Set Tenant Created', props<{ value: boolean }>());
export const updateTenant = createAction(
  '[TENANT] Update Tenant',
  props<{ payload: UpdateTenant; blobImage?: Blob }>(),
);
export const updateTenantSuccess = createAction('[TENANT] Update Tenant Success');
export const updateTenantNameSuccess = createAction('[TENANT] Update Tenant Name Success', props<{ value: string }>());
export const uploadOrgLogo = createAction(
  '[TENANT] Add Logo',
  props<{ tenantId: string; blobImage: Blob; preOperation: UploadLogoPreOperation }>(),
);
export const uploadOrgLogoSuccess = createAction('[TENANT] Upload org logo Success');
export const getOrgLogo = createAction('[TENANT] Get Logo', props<{ tenantId: string }>());
export const getOrgLogoSuccess = createAction('[TENANT] Get org logo Success');
export const generateInviteCode = createAction('[TENANT] Generate Invite Code', props<{ tenantId: string }>());
export const generateInviteCodeSuccess = createAction('[TENANT] Generate Invite Code Success');
export const noLastAccessedTenant = createAction('[TENANT] No Last Accessed Tenant');
export const setPreferences = createAction('[PREFERENCES] Set Preferences', props<{ tenantId: string }>());
export const setPreferencesSuccess = createAction(
  '[PREFERENCES] Set Preferences Success',
  props<{ tenantId: string }>(),
);

//**
export const setFavoriteApps = createAction(
  '[PREFERENCES] Set Favorite apps preferences',
  props<{ favoriteApps: FavoriteApps }>(),
);
export const setFavoriteAppsSuccess = createAction(
  '[PREFERENCES] Set Preferences Success',
  props<{ favoriteApps: FavoriteApps }>(),
);

export const processPreferences = createAction('[PREFERENCES] Process Preferences', props<{ payload?: Preferences }>());
export const processPreferencesSuccess = createAction('[PREFERENCES] Process Preferences Success');
export const shareFeedback = createAction('[USER DATA] Share User Feedback', props<{ payload: UserFeedback }>());
export const shareFeedbackSuccess = createAction('[USER DATA] Share User Feedback Success');
export const getAllTenantResourceRoles = createAction(
  '[TENANT RESOURCE ROLES] Get Tenant Resource Roles',
  props<{ payload: string }>(),
);
export const setAllTenantResourceRolesSuccess = createAction(
  '[TENANT RESOURCE ROLES] Set Tenant Resource Roles Success',
  props<{ payload: UserRole[] }>(),
);

export const getUserRolesForTenant = createAction(
  '[USER RESOURCE ROLES FOR TENANT] Get User Resource Roles For Tenant',
  props<{ tenantId: string; userId: string }>(),
);

export const setUserRolesForTenantSuccess = createAction(
  '[USER RESOURCE ROLES FOR TENANT] Set User Resource Roles For Tenant Success',
  props<{ payload: UserRole[] }>(),
);

export const getUserResources = createAction('[RESOURCES] Get User Resources', props<{ value: string }>());
export const setUserResourcesSuccess = createAction(
  '[RESOURCES] Set User Resources Success',
  props<{ payload: any }>(),
);

export const getSuggestedTenantByCode = createAction(
  '[TENANT BY CODE] Suggest Tenant By Code',
  props<{ inviteCode: string }>(),
);
export const setSuggestedTenantByCodeSuccess = createAction(
  '[TENANT BY CODE] Suggest Tenant By Code Success',
  props<{ payload: ITenantDetail }>(),
);

export const setNoSuggestedTenantByCodeFound = createAction(
  '[TENANT BY CODE] No Suggested Tenant By Code',
  props<{ payload: boolean }>(),
);

export const createSelectedFakeEntitlements = createAction(
  '[ENTITLEMENTS] Create selected fake entitlements',
  props<{ codes: ICatalogInfo[]; quantity: number; duration: number; effectiveDate: string }>(),
);
export const createSelectedFakeEntitlementsSuccess = createAction(
  '[ENTITLEMENTS] Create selected fake entitlements Success',
  props<{ value: boolean }>(),
);
export const applyRegularEntitlement = createAction(
  '[ENTITLEMENTS] Apply Regular entitlement',
  props<{
    userId: string;
    tenantId: string;
    entitlementData: EntitlementData;
    payload: AllocateEntitlementPayload;
    domain: string;
    organizationName?: string;
  }>(),
);
export const applyCustomProvisioningEntitlement = createAction(
  '[ENTITLEMENTS] Apply Custom entitlement',
  props<{
    userId: string;
    tenantId: string;
    entitlementData: EntitlementData;
    payload: AllocateEntitlementPayload;
    domain: string;
  }>(),
);
export const applyEntitlementSuccess = createAction('[ENTITLEMENTS] Apply entitlement Success');
export const deallocateEntitlement = createAction(
  '[ENTITLEMENTS] Deallocate entitlement',
  props<{
    tenantId: string;
    entitlement: Entitlement;
    requestBody: DeallocateEntitlementRequestBody;
    organizationName?: string;
  }>(),
);
export const addAccess = createAction(
  '[ADD ACCESS] Add Access',
  props<{ value: ResourceRoleAction; tenantId: string; flow?: 'add' | 'edit' | 'approve' }>(),
);
export const addAccessSuccess = createAction('[ADD ACCESS] Add Access Success');
export const updateAccess = createAction(
  '[Update ACCESS] Update Access',
  props<{ payload: ResourceRoleAction[]; tenantId: string; flow?: 'add' | 'edit' | 'approve' }>(),
);
export const removeAccess = createAction('[REMOVE ACCESS] Remove Access', props<{ payload: ResourceRoleAction }>());
export const approveAccess = createAction(
  '[APPROVE ACCESS] Approve Access',
  props<{ payload: ResourceRoleAction; userData: any }>(),
);

export const setEditUserDetails = createAction(
  '[EDIT USER DETAILS] Edit User Details',
  props<{ payload: Partial<UserRole> }>(),
);

export const getTenantServices = createAction(
  '[TENANT SERVICES] Get Current Tenant Services',
  props<{ payload: UserTenant }>(),
);
export const setTenantServices = createAction(
  '[TENANT SERVICES] Set Current Tenant Services Success',
  props<{ payload: TenantService[] }>(),
);

export const getTenantUtilityTokens = createAction(
  '[TENANT UTILITY TOKENS] Get Current Tenant Utility Tokens and Entitlements',
  props<{ value: string }>(),
);

export const setTenantUtilityTokens = createAction(
  '[TENANT UTILITY TOKENS] Set Current Tenant Utility Tokens',
  props<{ payload: TenantUtilityTokens }>(),
);

export const openIframe = createAction(
  '[CUSTOM PROVISIONING] Open Iframe for custom Provisioning',
  props<{ payload: PendingService[] }>(),
);
export const initializeCustomProvisioning = createAction(
  '[CUSTOM PROVISIONING] Start Custom Provisioning',
  props<{ payload: EntitlementData }>(),
);

export const setCurrentProvisioningData = createAction(
  '[CUSTOM PROVISIONING] Set Current Provisioning Data',
  props<{ payload: any }>(),
);

export const waitForCustomProvisioningComplete = createAction(
  '[CUSTOM PROVISIONING] Wait For Custom Provisioning Complete',
  props<{ payload: EntitlementData }>(),
);
export const customProvisioningCompleted = createAction('[CUSTOM PROVISIONING] Custom Provisioning Completed');
export const leaveCustomProvisioning = createAction('[CUSTOM PROVISIONING] Leave Custom Provisioning');

export const applyEntitlementCancelled = createAction('[CUSTOM PROVISIONING] Custom Provisioning Cancelled');

export const waitForCommadRequestResponse = createAction(
  '[COMMAND REQUEST] Wait For Command Request Response',
  props<{ payload: CommandRequestData }>(),
);

export const cardClicked = createAction(
  '[CARD CLICKED] Appliction card clicked',
  props<{ action: ApplicationCardButtonAction; card: ApplicationCard }>(),
);

export const getTenantUsers = createAction(
  '[TENANT] Get selected users of a Tenant',
  props<{ payload: TenantHierarchyUsersRequest }>(),
);

export const setTenantUsers = createAction(
  '[TENANT] Set selected users of a Tenant',
  props<{ selectedTenantUsers: PageResponse<TenantUser> }>(),
);

export const unarchiveTenant = createAction('[TENANT] Unarchive Tenant', props<{ tenant: AccessibleTenant }>());
export const unarchiveTenantSuccess = createAction('[TENANT] Unarchive Tenant Success');

export const archiveTenant = createAction(
  '[TENANT] Archive Tenant',
  props<{ archiveTenantPayload: ArchiveTenantPayload }>(),
);
export const archiveTenantSuccess = createAction('[TENANT] Archive Tenant Success');

export const refreshUserEntitlementsGrid = createAction('[ENTITLEMENTS] Refresh User Entitlements Grid');
export const clearUserEntitlementsGridRefresh = createAction('[ENTITLEMENTS] Clear User Entitlements Grid');
export const refreshOrgEntitlements = createAction(
  '[ENTITLEMENTS] Refresh Org Entitlements',
  props<{ value: string }>(),
);
export const clearOrgEntitlementsRefresh = createAction('[ENTITLEMENTS] Clear Org Entitlements refresh');

export const refreshUserRolesGrid = createAction('[USER ROLES] Refresh User Roles Grid');
export const clearUserRolesGridRefresh = createAction('[USER ROLES] Clear User Roles Grid refresh');

export const getUnallocatedEntitlementsCount = createAction('[ENTITLEMENTS] Get UnAllocated Entitlements Count');

export const setUnallocatedEntitlementsCount = createAction(
  '[ENTITLEMENTS] Set UnAllocated Entitlements Count',
  props<{ payload: UnallocatedEntitlementCounts }>(),
);

export const setAllocateEntitlementResponseData = createAction(
  '[Allocate Entitelement] Set Allocate Entitelement Responce Data',
  props<{ payload: { errorData: AllocateEntitlementResponse } }>(),
);
export const resetAllocateEntitlementResponseData = createAction(
  '[Allocate Entitelement] reset Allocate Entitelement Response Data array',
);

export const refreshTenantListGrid = createAction('[TENANTS] Refresh Tenant List Grid');
export const clearRefreshTenantListGrid = createAction('[TENANTS] Clear Refresh Tenant List Grid');

export const leaveTenant = createAction('[LEAVE TENANT] Leave Tenant', props<{ payload: LeaveTenantPayload }>());

export const transferOwnership = createAction('[TENANT] Transfer Ownership', props<{ payload: TransferOwnership }>());

export const changeParentOrganization = createAction(
  '[TENANT] Change Parent Organization',
  props<{ payload: ChangeParentPayload; idempotencyKey: string }>(),
);
export const changeParentOrganizationSuccess = createAction('[TENANT] Change Parent Organization Success');

export const checkUserCanChangeTenantHierarchy = createAction(
  '[TENANT] Check User Can Change Tenant Hierarchy',
  props<{ tenantId: string; userId: string }>(),
);

export const setCanChangeTenantHierarchy = createAction(
  '[TENANT] Set User Can Change Tenant Hierarchy',
  props<{ value: boolean }>(),
);

export const convertToSubOrganization = createAction(
  '[TENANT] Convert To Sub Organization',
  props<{ payload: ConvertToSubOrganization }>(),
);

export const convertToSubOrganizationSuccess = createAction('[TENANT] Convert To Sub Organization Success');

export const convertToParentOrganization = createAction(
  '[TENANT] Convert To Parent Organization',
  props<{ payload: ConvertToParentOrganization }>(),
);
export const convertToParentOrganizationSuccess = createAction('[TENANT] Convert To Parent Organization Success');

export const userLosesAccessOnParentTenant = createAction('[TENANT] User Loses Access On Parent Tenant');

export const getUserMembership = createAction(
  '[TENANT] Get User Membership On Tenant',
  props<{ tenantId: string; userId: string }>(),
);

export const setUserMembership = createAction(
  '[TENANT] Set User Membership On Tenant',
  props<{ payload: UserMembership }>(),
);

export const navigateBasedOnParentRole = createAction(
  '[TENANT] Navigate Based On Parent Role',
  props<{ targetTenantId: string; refreshTenantDetails?: boolean; switchToParentTenant?: boolean }>(),
);

export const updateAccessibleTenants = createAction(
  '[TENANT] Update Accessible Tenant',
  props<{ accessibleTenants: AccessibleTenant[] }>(),
);

export const getTenantEffectiveRoles = createAction(
  '[TENANT] Get Tenant Effective Roles',
  props<{ tenantId: string; userId: string }>(),
);

export const setUserPermissionsEvalResult = createAction(
  '[USER] Set Users Evaluated Permission',
  props<{ result: PermsEvaluationResult }>(),
);

export const getLoggedInUserRoleHierarchy = createAction(
  '[USER ACCESS] Get Logged-In User Role Hierarchy',
  props<{ tenantId: string }>(),
);

export const setLoggedInUserRoleHierarchy = createAction(
  '[USER ACCESS] Set Logged-In User Role Hierarchy',
  props<{ userRoleHierarchy: UserRoleHierarchy }>(),
);

export const getTenantResourceHierarchy = createAction(
  '[USER ACCESS] Get Tenant Resource Hierarchy',
  props<{ tenantId: string }>(),
);

export const setTenantResourceHierarchy = createAction(
  '[USER ACCESS] Set Tenant Resource Hierarchy',
  props<{ resourceHierarchy: TenantResourceHierarchy }>(),
);

export const updateUserDescriptionForAllocationId = createAction(
  '[TENANT] Update User Description For Entitlement Allocation Id',
  props<{
    allocationId: string;
    entitlementId: string;
    userDescription: string;
    tenantId: string;
  }>(),
);

export const updateUserDescriptionOnSuccess = createAction(
  '[TENANT] Update User Description For Entitlement AllocationId Success',
  props<{ allocationId: string; entitlementId: string; userDescription: string }>(),
);

export const updateUserDescriptionOnFailure = createAction(
  '[TENANT] Update User Description For Entitlement AllocationId Failure',
  props<{ allocationId: string; entitlementId: string }>(),
);
